
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ArchiveOperation} from '../inner/ArchiveOperation';
import {FieldModelArchiveOperation} from '../inner/ArchiveOperation';

/**
 * AuxiliaryAccountInfoArchiveNotify struct
 */
class AuxiliaryAccountInfoArchiveNotify {
  /**
   * Initialize struct
   * @param {!number=} account
   * @param {!ArchiveOperation=} operationType
   * @constructor
   */
  constructor (account = new UInt64(0, 0), operationType = new ArchiveOperation()) {
    this.Account = account
    this.OperationType = operationType
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AuxiliaryAccountInfoArchiveNotify}
   * @param {!AuxiliaryAccountInfoArchiveNotify} other Other struct
   * @returns {!AuxiliaryAccountInfoArchiveNotify} This struct
   */
  copy (other) {
    if (other.Account != null) {
      this.Account = UInt64.fromNumber(other.Account)
    } else {
      this.Account = null
    }
    if (other.OperationType != null) {
      this.OperationType = ArchiveOperation.fromObject(other.OperationType)
    } else {
      this.OperationType = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AuxiliaryAccountInfoArchiveNotify}
   * @returns {!AuxiliaryAccountInfoArchiveNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AuxiliaryAccountInfoArchiveNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AuxiliaryAccountInfoArchiveNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AuxiliaryAccountInfoArchiveNotify}
   * @param {!AuxiliaryAccountInfoArchiveNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AuxiliaryAccountInfoArchiveNotify)) {
      throw new TypeError('Instance of AuxiliaryAccountInfoArchiveNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AuxiliaryAccountInfoArchiveNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Account: ((this.Account != null) ? this.Account.toNumber() : null),
      OperationType: ((this.OperationType != null) ? this.OperationType : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AuxiliaryAccountInfoArchiveNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AuxiliaryAccountInfoArchiveNotify} other Object value
   * @returns {!AuxiliaryAccountInfoArchiveNotify} Created struct
   */
  static fromObject (other) {
    return new AuxiliaryAccountInfoArchiveNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AuxiliaryAccountInfoArchiveNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10052
  }
}

export { AuxiliaryAccountInfoArchiveNotify };

/**
 * Fast Binary Encoding AuxiliaryAccountInfoArchiveNotify field model
 */
class FieldModelAuxiliaryAccountInfoArchiveNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Account = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.OperationType = new FieldModelArchiveOperation(buffer, this.Account.fbeOffset + this.Account.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Account.fbeSize + this.OperationType.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Account.fbeExtra + this.OperationType.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAuxiliaryAccountInfoArchiveNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10052
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Account.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Account.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Account.fbeSize

    if ((fbeCurrentSize + this.OperationType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OperationType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OperationType.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {!AuxiliaryAccountInfoArchiveNotify} fbeValue Default value, defaults is new AuxiliaryAccountInfoArchiveNotify()
   * @returns {!AuxiliaryAccountInfoArchiveNotify} AuxiliaryAccountInfoArchiveNotify value
   */
  get (fbeValue = new AuxiliaryAccountInfoArchiveNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {!AuxiliaryAccountInfoArchiveNotify} fbeValue AuxiliaryAccountInfoArchiveNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Account.fbeSize) <= fbeStructSize) {
      fbeValue.Account = this.Account.get()
    } else {
      fbeValue.Account = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Account.fbeSize

    if ((fbeCurrentSize + this.OperationType.fbeSize) <= fbeStructSize) {
      fbeValue.OperationType = this.OperationType.get()
    } else {
      fbeValue.OperationType = new ArchiveOperation()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OperationType.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {!AuxiliaryAccountInfoArchiveNotify} fbeValue AuxiliaryAccountInfoArchiveNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAuxiliaryAccountInfoArchiveNotify}
   * @param {AuxiliaryAccountInfoArchiveNotify} fbeValue AuxiliaryAccountInfoArchiveNotify value
   */
  setFields (fbeValue) {
    this.Account.set(fbeValue.Account)
    this.OperationType.set(fbeValue.OperationType)
  }
}

export { FieldModelAuxiliaryAccountInfoArchiveNotify };

/**
 * Fast Binary Encoding AuxiliaryAccountInfoArchiveNotify model
 */
class AuxiliaryAccountInfoArchiveNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAuxiliaryAccountInfoArchiveNotify(this.buffer, 4)
  }

  /**
   * Get the AuxiliaryAccountInfoArchiveNotify model
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @returns {!FieldModelAuxiliaryAccountInfoArchiveNotify} model AuxiliaryAccountInfoArchiveNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AuxiliaryAccountInfoArchiveNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAuxiliaryAccountInfoArchiveNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @param {!AuxiliaryAccountInfoArchiveNotify} value AuxiliaryAccountInfoArchiveNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @param {!AuxiliaryAccountInfoArchiveNotify} value AuxiliaryAccountInfoArchiveNotify value, defaults is new AuxiliaryAccountInfoArchiveNotify()
   * @return {!object} Deserialized AuxiliaryAccountInfoArchiveNotify value and its size
   */
  deserialize (value = new AuxiliaryAccountInfoArchiveNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AuxiliaryAccountInfoArchiveNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AuxiliaryAccountInfoArchiveNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AuxiliaryAccountInfoArchiveNotifyModel}
   * @param {!number} prev Previous AuxiliaryAccountInfoArchiveNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AuxiliaryAccountInfoArchiveNotifyModel };
